(ns ui.editor2.sidebar-captions
  "The Captions entry of the editor2 sidebar: transcribe a video clip,
   give it captions in one of the styles and correct the words and
   their timestamps. The panel works on the selected video clip (or the
   one picked in it)."
  (:require [parts.replicant.command :as command]
            [ui.captions.editor :as editor]
            [ui.captions.pricing :as pricing]
            [ui.captions.query :as cap]
            [ui.captions.style-picker :as style-picker]
            [ui.captions.styles :as styles]))

(def entry-class
  ["flex" "pt-3" "pb-3" "pl-2" "pr-2" "flex-col" "justify-center" "items-center"
   "gap-1" "rounded-lg" "cursor-pointer" "hover:bg-gray-600"])

(defn icon
  [selected?]
  [:svg {:xmlns "http://www.w3.org/2000/svg"
         :fill "none"
         :viewBox "0 0 24 24"
         :stroke-width "1.5"
         :stroke "currentColor"
         :class ["size-6" (when-not selected? "text-slate-400")]}
   [:rect {:x "2.75" :y "5.25" :width "18.5" :height "13.5" :rx "2.5"}]
   [:path {:stroke-linecap "round"
           :d "M10.5 10.25a2.25 2.25 0 1 0 0 3.5M17 10.25a2.25 2.25 0 1 0 0 3.5"}]])

(defn entry
  [selected?]
  [:div
   {:class (conj entry-class (when selected? "bg-gray-800"))
    :on {:click [[:store/assoc-in [:editor2 :sidebar-panel] :captions-sidebar]
                 [:data/query pricing/pricing-query]]}}
   (icon selected?)
   [:span
    {:class ["text-center" "text-xs" "font-medium" (when-not selected? "text-slate-400")]}
    "Captions"]])

(def input-class
  ["rounded" "border-gray-900" "text-white" "shadow-sm" "text-xs" "px-2" "py-1"])

(def input-style
  {:background-color "oklch(0.21 0.034 264.665)"})

(def button-class
  ["rounded" "bg-indigo-600" "hover:bg-indigo-500" "disabled:opacity-50"
   "text-white" "text-xs" "font-medium" "px-3" "py-1.5"])

(def icon-button-class
  ["text-slate-400" "hover:text-white" "text-xs" "px-1"])

(defn clip-label
  [clip]
  (or (:clip/name clip)
      (get-in clip [:clip/asset :asset/name])
      "clip"))

(defn transcribing?
  [state]
  (when-let [cmd (get-in state [:editor2 :captions-sidebar :command])]
    (command/issued? state cmd)))

(defn source-select
  "The picked option carries `:selected`: Replicant sets the `:value`
   of a <select> before its options exist, where it has no effect."
  [state]
  (let [current (cap/source-clip state)]
    (into [:select {:class (conj input-class "w-full")
                    :style input-style
                    :on {:change [[:captions/set-source :event/target.value]]}}]
          (map (fn [clip]
                 [:option {:value (str (:clip/uuid clip))
                           :selected (= (:clip/uuid clip) (:clip/uuid current))}
                  (clip-label clip)])
               (cap/media-clips state)))))

(defn transcribe-button
  "Waits for the upload of a media added in this session: the server
   can only read it once it has an upload URI."
  [state label]
  (let [busy? (transcribing? state)
        clip (cap/source-clip state)
        uploading? (and clip
                        (nil? (cap/source-url clip)))]
    [:button {:type "button"
              :disabled (boolean (or busy?
                                     uploading?
                                     (nil? clip)))
              :class button-class
              :on {:click [[:captions/transcribe]]}}
     (cond
       busy? "Transcribing…"
       uploading? "Uploading…"
       :else label)]))

(defn price-line
  "What the transcription of the clip's media will cost, once the price
   query answered."
  [state]
  (when-let [label (pricing/estimate-label state (cap/source-clip state))]
    [:p {:class ["text-xs" "text-slate-400"]} label]))

(defn error-line
  [state]
  (when-let [error (get-in state [:editor2 :captions-sidebar :error])]
    [:p {:class ["text-xs" "text-red-400"]} error]))

(defn source-section
  [state label]
  (if (seq (cap/media-clips state))
    [:div {:class ["flex" "flex-col" "gap-2"]}
     [:span {:class ["text-xs" "font-medium"]} "Clip with speech"]
     (source-select state)
     (transcribe-button state label)
     (price-line state)
     (error-line state)]
    [:p {:class ["text-xs" "text-slate-400"]}
     "Add a video clip with speech first."]))

(defn start-panel
  [state]
  (let [source (cap/source-clip state)
        transcribed? (seq (get-in source [:clip/asset :asset/words]))]
    [:div {:class ["flex" "flex-col" "gap-3"]}
     [:p {:class ["text-xs" "text-slate-400"]}
      "Captions transcribe the speech of a clip and show the words as they are spoken."]
     (when transcribed?
       [:div {:class ["flex" "flex-col" "gap-2"]}
        [:p {:class ["text-xs" "text-slate-300"]}
         "This clip already has a transcript."]
        [:button {:type "button"
                  :class button-class
                  :on {:click [[:captions/add]]}}
         "Add captions"]])
     (source-section state (if transcribed? "Transcribe again" "Transcribe"))]))

(defn remove-button
  []
  [:button {:type "button"
            :class (conj icon-button-class "self-start")
            :on {:click [[:captions/remove]]}}
   "Remove captions"])

(defn style-section
  [state clip]
  (let [current-id (cap/style clip)
        current (styles/find-style current-id)
        open? (get-in state [:editor2 :captions-sidebar :style-picker?])]
    [:div {:class ["flex" "flex-col" "gap-2"]}
     [:div {:class ["flex" "items-center" "justify-between" "gap-2"]}
      [:span {:class ["text-xs" "font-medium"]}
       (str "Style: " (or (:name current) current-id))]
      [:button {:type "button"
                :class icon-button-class
                :on {:click [[:store/assoc-in
                              [:editor2 :captions-sidebar :style-picker?]
                              (not open?)]]}}
       (if open? "Close" "Change")]]
     (when-not open?
       [:p {:class ["text-xs" "text-slate-400"]} (:description current)])
     (when open?
       (style-picker/grid clip current-id))]))

(defn seconds
  [ms]
  (/ (js/Math.round (/ ms 10)) 100))

(defn time-input
  [word attribute]
  [:input {:type "number"
           :step "0.01"
           :min "0"
           :class (conj input-class "w-20")
           :style input-style
           :value (seconds (get word attribute))
           :on {:change [[:captions/set-word (:word/uuid word) attribute :event/target.value]]}}])

(defn selected-word
  "The word of the cursor in the transcript editor, when the captions
   clip still shows it."
  [state words]
  (let [word-uuid (get-in state [:editor2 :captions-sidebar :selected-word])]
    (some (fn [word]
            (when (= word-uuid (:word/uuid word))
              word))
          words)))

(defn word-strip
  "The times and the emphasis of the word at the cursor."
  [word]
  [:div {:class ["flex" "items-center" "gap-1" "flex-wrap"]}
   [:span {:class ["text-xs" "text-slate-300" "truncate" "max-w-24"]
           :title (:word/text word)}
    (:word/text word)]
   (time-input word :word/start)
   [:span {:class ["text-xs" "text-slate-500"]} "–"]
   (time-input word :word/end)
   [:span {:class ["text-xs" "text-slate-500"]} "s"]
   [:button {:type "button"
             :title "Emphasize (Ctrl+B / Cmd+B)"
             :class (conj icon-button-class (when (:word/emphasis? word) "text-yellow-400"))
             ;; Keeps the focus and the selection in the transcript:
             :on {:mousedown [[:js/prevent-default]]
                  :click [[:captions/toggle-emphasis]]}}
    "★"]])

(defn words-editor
  "The transcript of the clip as text: the words of the media that fall
   into the clip (see `ui.captions.editor`)."
  [state clip]
  (let [words (cap/clip-stored-words clip)
        total (count (cap/transcript (:clip/asset clip)))
        word (selected-word state words)]
    [:div {:class ["flex" "flex-col" "gap-2"]}
     [:span {:class ["text-xs" "font-medium"]}
      (str "Transcript (" (count words)
           (when (not= total (count words))
             (str " of " total))
           " words)")]
     [:style editor/css]
     (editor/element {:clip-uuid (:clip/uuid clip)
                      :clip-range (cap/media-range clip)
                      :words words
                      :playhead-ms (cap/media-time state clip)
                      :dispatch! (:replicant/dispatch! state)})
     (if word
       (word-strip word)
       [:p {:class ["text-xs" "text-slate-400"]}
        "Correct the text like in a text field, the words keep their timing. "
        "Click a word to jump to it."])
     [:p {:class ["text-gray-400" "font-mono"]
          :style {:font-size "10px"}}
      "New phrase: Enter" [:br]
      "Emphasize: Ctrl+B (Cmd+B)" [:br]
      "Undo: Ctrl+Z (Cmd+Z)"]]))

(defn editor-panel
  [state clip]
  [:div {:class ["flex" "flex-col" "gap-4"]}
   (style-section state clip)
   (source-section state "Transcribe again")
   (words-editor state clip)
   (remove-button)])

(defn load-pricing-on-mount
  "Runs the price query when the panel opens without it."
  [state]
  (fn [_]
    (when-not (pricing/pricing state)
      ((:replicant/dispatch! state) {:actions [[:data/query pricing/pricing-query]]}))))

(defn panel
  "Scrolls on its own (the transcript is long), so the content keeps a
   margin to its scrollbar."
  [state]
  [:div {:class ["flex" "flex-col" "gap-3" "w-full" "overflow-y-auto" "h-full" "pb-8" "pr-3"]
         :replicant/on-mount (load-pricing-on-mount state)}
   [:span {:class ["text-xs" "font-medium"]} "Captions"]
   (let [clip (cap/source-clip state)]
     (if (cap/captioned? clip)
       (editor-panel state clip)
       (start-panel state)))])
